import {
    enumReduce,
    stringGenerateUnique,
    stringTrimPrefix,
    urlJoin,
    urlOverrideQueryParams,
    valueConvertWhenNotNullable,
    Values
} from '@wix/devzai-utils-common';
import {DoppeSupportedWixTpas} from './doppe-supported-wix-tpas';
import {DoppeViewerLang} from "../doppe-viewer/doppe-viewer-lang";

export const DoppeHtmlSiteUriMappingType = {
    Default: 'Default',
    Store: 'Store',
    Services: 'Services',
    Ecom: 'Ecom',
    Blog: 'Blog',
    Events: 'Events',
    PricingPlans: 'PricingPlans'
} as const;

export type DoppeHtmlSiteUriMappingType = Values<typeof DoppeHtmlSiteUriMappingType>;

const DoppeHtmlSiteUriMappingTypesSuggestedBasePaths = {
    [DoppeHtmlSiteUriMappingType.Default]: ['page', '_'],
    [DoppeHtmlSiteUriMappingType.Store]: ['store', 'online-store', 'shop'],
    [DoppeHtmlSiteUriMappingType.Ecom]: ['ecom', 'ecommerce'],
    [DoppeHtmlSiteUriMappingType.Blog]: ['blog', 'articles', 'posts'],
    [DoppeHtmlSiteUriMappingType.Services]: ['services', 'bookings', 'appointments', 'booking-services'],
    [DoppeHtmlSiteUriMappingType.Events]: ['events', 'event', 'happenings', 'calendar'],
    [DoppeHtmlSiteUriMappingType.PricingPlans]: ['pricing', 'plans', 'membership', 'subscriptions']
} satisfies Record<DoppeHtmlSiteUriMappingType, string[]>;

export function doppeHtmlSiteUriMappingTypeGetSuggestedBasePath (
    uriMappingType: DoppeHtmlSiteUriMappingType,
    options: {
        usedBasePaths: string[];
    }
): string {
    const {
        usedBasePaths
    } = options;

    return stringGenerateUnique(DoppeHtmlSiteUriMappingTypesSuggestedBasePaths[uriMappingType], usedBasePaths, (iteration, value) => {
        if (iteration === 0) {
            return `_${value}`;
        } else if (iteration === 1) {
            return `__${value}`;
        } else {
            return `${value}${iteration - 1}`
        }
    })
}

export const DoppeHtmlSiteRequiringWixAppsIds = enumReduce(DoppeSupportedWixTpas, [
    DoppeSupportedWixTpas.StoresNew,
    DoppeSupportedWixTpas.Stores,
    DoppeSupportedWixTpas.Blog,
    DoppeSupportedWixTpas.Booking,
    DoppeSupportedWixTpas.Events,
    DoppeSupportedWixTpas.PricingPlans,
])

export type DoppeHtmlSiteRequiringWixAppsIds = Values<typeof DoppeHtmlSiteRequiringWixAppsIds>;

const WixAppsIdsToHtmlSiteUriMappingType = {
    [DoppeHtmlSiteRequiringWixAppsIds.StoresNew]: DoppeHtmlSiteUriMappingType.Store,
    [DoppeHtmlSiteRequiringWixAppsIds.Stores]: DoppeHtmlSiteUriMappingType.Ecom,
    [DoppeHtmlSiteRequiringWixAppsIds.Blog]: DoppeHtmlSiteUriMappingType.Blog,
    [DoppeHtmlSiteRequiringWixAppsIds.Booking]: DoppeHtmlSiteUriMappingType.Services,
    [DoppeHtmlSiteRequiringWixAppsIds.Events]: DoppeHtmlSiteUriMappingType.Events,
    [DoppeHtmlSiteRequiringWixAppsIds.PricingPlans]: DoppeHtmlSiteUriMappingType.PricingPlans,
} satisfies Record<DoppeHtmlSiteRequiringWixAppsIds, DoppeHtmlSiteUriMappingType>;

export function dopppeHtmlSiteUriMappingTypesResolveFromWixAppId (
    wixAppId: DoppeHtmlSiteRequiringWixAppsIds
): DoppeHtmlSiteUriMappingType {
    return WixAppsIdsToHtmlSiteUriMappingType[wixAppId];
}

export type DoppeHtmlSiteUriMappings = Partial<Record<DoppeHtmlSiteUriMappingType, string>>;

export type DoppeHtmlSiteInfo = {
    htmlSiteUriMapping: DoppeHtmlSiteUriMappings;
    htmlSiteUrl: string;
}

export function doppeHtmlSiteResolvePageUrlFromBusinessEntityPageUrl (
    options: {
        doppeHtmlSiteInfo: DoppeHtmlSiteInfo;
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType;
        businessEntityPageUrl: string;
        viewerLang: DoppeViewerLang
    }
) {
    const {
        doppeHtmlSiteInfo,
        htmlSiteUriMappingType,
        businessEntityPageUrl,
        viewerLang
    } = options;

    return doppeHtmlSiteResolvePageUrl({
        doppeHtmlSiteInfo: doppeHtmlSiteInfo,
        htmlSiteUriMappingType: htmlSiteUriMappingType,
        pagePath: stringTrimPrefix(businessEntityPageUrl, doppeHtmlSiteInfo.htmlSiteUrl),
        viewerLang: viewerLang
    })
}

export function doppeHtmlSiteResolveBlogPostPageUrl (
    options: {
        doppeHtmlSiteInfo: DoppeHtmlSiteInfo;
        slug: string;
        viewerLang: DoppeViewerLang;
    }
) {
    return doppeHtmlSiteResolvePageUrlFromBusinessEntityPageUrl({
        doppeHtmlSiteInfo: options.doppeHtmlSiteInfo,
        businessEntityPageUrl: `post/${options.slug}`,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.Blog,
        viewerLang: options.viewerLang
    })
}


export function doppeHtmlSiteResolveEventPageUrl (
    options: {
        doppeHtmlSiteInfo: DoppeHtmlSiteInfo;
        slug: string;
        viewerLang: DoppeViewerLang;
    }
) {
    return doppeHtmlSiteResolvePageUrlFromBusinessEntityPageUrl({
        doppeHtmlSiteInfo: options.doppeHtmlSiteInfo,
        businessEntityPageUrl: `event-details/${options.slug}`,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.Services,
        viewerLang: options.viewerLang
    })
}

export function doppeHtmlSiteResolvePricingPlanPageUrl (
    options: {
        doppeHtmlSiteInfo: DoppeHtmlSiteInfo;
        viewerLang: DoppeViewerLang;
    }
) {
    return doppeHtmlSiteResolvePageUrlFromBusinessEntityPageUrl({
        doppeHtmlSiteInfo: options.doppeHtmlSiteInfo,
        businessEntityPageUrl: `plans-pricing`,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.PricingPlans,
        viewerLang: options.viewerLang
    })
}

export function doppeHtmlSiteResolveServicePageUrl (
    options: {
        doppeHtmlSiteInfo: DoppeHtmlSiteInfo;
        pageURL: string;
        viewerLang: DoppeViewerLang;
    }
) {
    return doppeHtmlSiteResolvePageUrlFromBusinessEntityPageUrl({
        doppeHtmlSiteInfo: options.doppeHtmlSiteInfo,
        businessEntityPageUrl: options.pageURL,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.Services,
        viewerLang: options.viewerLang
    })
}

export function doppeHtmlSiteResolveProductPageUrl (
    options: {
        doppeHtmlSiteInfo: DoppeHtmlSiteInfo;
        productPageUrl: string;
        viewerLang: DoppeViewerLang;
    }
) {
    return doppeHtmlSiteResolvePageUrlFromBusinessEntityPageUrl({
        doppeHtmlSiteInfo: options.doppeHtmlSiteInfo,
        businessEntityPageUrl: options.productPageUrl,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.Store,
        viewerLang: options.viewerLang
    })
}

export function doppeHtmlSiteResolvePageUrl (
    options: {
        doppeHtmlSiteInfo: DoppeHtmlSiteInfo;
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType;
        pagePath: string;
        viewerLang: DoppeViewerLang
    }
) {
    const {
        doppeHtmlSiteInfo,
        htmlSiteUriMappingType,
        pagePath,
        viewerLang
    } = options;

    return valueConvertWhenNotNullable(doppeHtmlSiteInfo.htmlSiteUriMapping[htmlSiteUriMappingType], uriMapping => {
        return urlOverrideQueryParams(urlJoin(doppeHtmlSiteInfo.htmlSiteUrl, uriMapping, pagePath), {
            lang: viewerLang === DoppeViewerLang.English ? null : viewerLang
        });
    }) ?? null
}

export function doppeHtmlSiteResolvePricingPlansPageURL (_doppeHtmlSiteInfo: DoppeHtmlSiteInfo, _viewerLang: DoppeViewerLang) {
    // ai should replace with the actual pricing plans page URL
    return '';
}

export function doppeHtmlSiteResolveEventsPageURL(doppeHtmlSiteInfo: DoppeHtmlSiteInfo, viewerLang: DoppeViewerLang) {
    return doppeHtmlSiteResolvePageUrl({
        doppeHtmlSiteInfo: doppeHtmlSiteInfo,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.Events,
        pagePath: '/event-list',
        viewerLang: viewerLang
    });
}

export function doppeHtmlSiteResolveAllPostsPageUrl (
    doppeHtmlSiteInfo: DoppeHtmlSiteInfo,
    viewerLang: DoppeViewerLang,
) {
    return doppeHtmlSiteResolvePageUrl({
        doppeHtmlSiteInfo: doppeHtmlSiteInfo,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.Blog,
        pagePath: '/blog',
        viewerLang: viewerLang
    })
}

export function doppeHtmlSiteResolveAllProductsPageUrl (
    doppeHtmlSiteInfo: DoppeHtmlSiteInfo,
    viewerLang: DoppeViewerLang
) {
    return doppeHtmlSiteResolvePageUrl({
        doppeHtmlSiteInfo: doppeHtmlSiteInfo,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.Store,
        pagePath: '/category/all-products',
        viewerLang: viewerLang
    })
}

export function doppeHtmlSiteResolveCartPageUrl (
    doppeHtmlSiteInfo: DoppeHtmlSiteInfo,
    viewerLang: DoppeViewerLang
) {
    return doppeHtmlSiteResolvePageUrl({
        doppeHtmlSiteInfo: doppeHtmlSiteInfo,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.Store,
        pagePath: '/cart-page',
        viewerLang: viewerLang
    })
}

export function doppeHtmlSiteResolveBookOnlinePageUrl (
    doppeHtmlSiteInfo: DoppeHtmlSiteInfo,
    viewerLang: DoppeViewerLang,
) {
    return doppeHtmlSiteResolvePageUrl({
        doppeHtmlSiteInfo: doppeHtmlSiteInfo,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.Services,
        pagePath: '/book-online',
        viewerLang: viewerLang
    })
}

export const DoppeHtmlSitePagesType = {
    Store: 'Store',
    Events: 'Events',
    BookingServices: 'BookingServices',
    Blog: 'Blog',
    PricingPlans: 'PricingPlans',
    Cart: 'Cart',
} as const;

export type DoppeHtmlSitePagesType = Values<typeof DoppeHtmlSitePagesType>;